/* isLineKey — the ONE per-line IS-key derivation.
 *
 * `slugifyGlName` was moved here VERBATIM from AdminPage.js (where it was private) so
 * that the Step-3 IS line-key suggestion and the Step-6 vendor per-line mapping share
 * one implementation instead of growing a second. Principle 0: 1 implementation → 1
 * implementation, never 2 (this is an EXTRACTION, and the three AdminPage call sites
 * switch to the import in the same commit).
 *
 * ★ THIS RULE IS LOAD-BEARING FOR REPRODUCIBILITY, not cosmetic. It is the JS twin of
 * the slug rule in scripts/parse-ae-model-vendor-template.py, which is how cid=3's 229
 * vendor rows got their `is_line` values. Measured equivalent on all 34 real
 * (is_category, gl_line_name) pairs in AE Model's template — so a tenant onboarded
 * through the wizard lands on the same keys the migration produced.
 *
 * ⚠ NOT the same job as the other slug-ish helpers in the tree, and they must not be
 * merged: `deptSlug` (utils/seatDepartments) builds DEPARTMENT identifiers with a
 * different rule ([^a-z0-9]+ → _), and inferIsMeta's `norm`/`tokens` are matching
 * helpers, not key builders.
 *
 * ★★ AND A THIRD JOB, ADDED HERE BECAUSE IT IS THE ONE MOST LIKELY TO BE "TIDIED" INTO
 * THIS FILE BY MISTAKE: `normalizeBSKey` (utils/bsKey.js) is the BALANCE-SHEET and
 * REVENUE key rule. It DELETES '&', '/', ',', '.' where slugifyGlName maps them to a
 * SPACE — "Discounts/Refunds Given" is `discountsrefunds_given` there and
 * `discounts_refunds_given` here — and the two disagree DELIBERATELY. ⛔ DO NOT UNIFY
 * THEM. Stored data is keyed under the bsKey rule (defrev_data.is_actuals,
 * revenue_module_mappings.gl_line_key, the bs_actuals_meta leaf keys), and there is no
 * Step-3 re-key guard on that path, so switching it to this rule is a DATA MIGRATION
 * across three storage sites — not a refactor. Analysis: diffs/phase2-bridge-phase0.txt §5.
 */

/* Slugify for a per-line vendor key — mirrors the Session 4a rules (CLAUDE.md §10.5)
 * that the historical template parser and inferIsMeta already use, so a NEW tenant's
 * generated keys match the ones an import would produce. */
export function slugifyGlName(name) {
    return (name || '')
        .toLowerCase()
        .replace(/<\s*\$?1000/g, 'under 1000')
        .replace(/[&/,.]/g, ' ')
        .replace(/</g, ' under ')
        .replace(/\$/g, '')
        .replace(/[^a-z0-9\s_]/g, ' ')
        .trim()
        .split(/\s+/)
        .filter(Boolean)
        .join('_');
}

/* The IS section prefix for a category's DISPLAY label.
 * ★ Includes 'Other' → 'oi', which AdminPage's PAYROLL_KEYS_BY_CAT deliberately omits
 * (a paytype key is never `oi_*`). Do not extend that map instead — different job. */
export const CAT_SLUG_BY_DISPLAY = {
    'COGS': 'cogs',
    'S&M': 'sm',
    'G&A': 'ga',
    'R&D': 'rd',
    'Other': 'oi',
};

/* The vendor template's accepted sections: exactly the keys above (Step 6 refuses anything else, case-sensitive).
 * Derived, never respelled, so the template spec and the Step 6 check cannot disagree. */
export const VENDOR_CATEGORY_LABELS = Object.keys(CAT_SLUG_BY_DISPLAY);

/* A vendor's per-line IS key: `${cat_slug}_${slugifyGlName(glLineName)}`.
 *
 * This is the composed form the Python migration used, verbatim from its header:
 *     cat_slug map: COGS->cogs, S&M->sm, G&A->ga, R&D->rd, Other->oi
 *     Vendor lines IGNORE display_group by design — per-line slug only
 *     (cat_slug + '_' + slugify(gl_line_name)).
 * Returns '' when either input is missing or the category is unknown — the caller
 * decides what to do with that (Step 6 flags the row for required review rather than
 * inventing a key; a fabricated key is the B3-12 ghost-leaf failure).
 */
export function buildVendorIsLineKey(isCategoryDisplay, glLineName) {
    const cat = CAT_SLUG_BY_DISPLAY[isCategoryDisplay];
    if (!cat) return '';
    const slug = slugifyGlName(glLineName);
    if (!slug) return '';
    return `${cat}_${slug}`;
}
